import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Trash2, X } from "lucide-react";
import { ConfirmButton } from "@/components/confirm-button";
import { Button } from "@/components/ui/button";
import { assetUrl } from "@/lib/assets";
import { IMAGE_NODE_WIDTH, type ImageNode } from "@/lib/board-graph";
import { cn } from "@/lib/utils";
import { useBoardData } from "./board-context";

/** An image the user uploaded; connects into prompts as a reference. */
export function ImageNodeView({ id, data, selected }: NodeProps<ImageNode>) {
	const { uploadsById, onRemoveNode, onDeleteUpload } = useBoardData();
	const upload = uploadsById.get(data.uploadId);

	return (
		<div
			className={cn(
				"group relative flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm",
				selected && "ring-2 ring-ring"
			)}
			style={{ width: IMAGE_NODE_WIDTH }}
		>
			<div className="flex aspect-[4/3] items-center justify-center bg-[repeating-conic-gradient(var(--color-muted)_0%_25%,transparent_0%_50%)] bg-size-[14px_14px] p-2">
				{upload ? (
					<img
						alt={upload.fileName ?? "Uploaded image"}
						className="max-h-full max-w-full rounded-sm shadow-xs"
						draggable={false}
						src={assetUrl(upload.assetId)}
					/>
				) : (
					<p className="text-muted-foreground text-xs">Image deleted</p>
				)}
			</div>
			<div className="flex items-center gap-1 border-t px-3 py-2">
				<div className="min-w-0 flex-1">
					<p className="truncate font-medium text-xs">
						{upload?.fileName ?? "Uploaded image"}
					</p>
					{upload && (
						<p className="truncate text-[11px] text-muted-foreground">
							{upload.width}×{upload.height}
						</p>
					)}
				</div>
				{upload && (
					<ConfirmButton
						aria-label="Delete image permanently"
						className="opacity-0 group-hover:opacity-100"
						confirmLabel={<Trash2 />}
						onConfirm={() => onDeleteUpload(upload.id)}
						size="icon-sm"
						title="Delete image permanently"
						variant="ghost"
					>
						<Trash2 />
					</ConfirmButton>
				)}
				<Button
					aria-label="Remove from board"
					className="opacity-0 group-hover:opacity-100"
					onClick={() => onRemoveNode(id)}
					size="icon-sm"
					variant="ghost"
				>
					<X />
				</Button>
			</div>
			<Handle position={Position.Right} type="source" />
		</div>
	);
}
